import Constants from 'js/Constants';
import {
  isRunIncompatibleSource,
  shouldRenderRunAction,
} from 'js/components/pipelines/cards/components/ExpandActions/utils/shouldRenderSourceAction';

export const FLOW_RUN_AFFORDANCE_ACTIVATE_WITH_CHOICE = 'activate-with-choice' as const;
export const FLOW_RUN_AFFORDANCE_ACTIVATE_ONLY = 'activate-only' as const;

export type FlowRunAffordance =
  | typeof FLOW_RUN_AFFORDANCE_ACTIVATE_WITH_CHOICE
  | typeof FLOW_RUN_AFFORDANCE_ACTIVATE_ONLY;

interface FlowRunAffordanceInput {
  isCapsuleFlow: boolean;
  source: Record<string, unknown> | null;
  connectionCategory?: string;
  status?: string | null;
}

// A flow with no status, INIT, or PAUSED hasn't finished a run yet, so it's still eligible for the Run Now / Next Cycle choice
// (NEX-21806 / S3 — INIT flows were wrongly excluded because the old check only recognized PAUSED).
export function isFlowAwaitingActivation(status?: string | null): boolean {
  if (!status) return true;
  return status !== Constants.SourceStatus.ACTIVE && status !== Constants.SourceStatus.RATE_LIMITED;
}

type FlowRunAffordanceRule = (input: FlowRunAffordanceInput) => FlowRunAffordance | null;

const alreadyRunningRule: FlowRunAffordanceRule = ({ status }) =>
  isFlowAwaitingActivation(status) ? null : FLOW_RUN_AFFORDANCE_ACTIVATE_ONLY;

// Excludes sources that are structurally incompatible with Run Now, for any reason unrelated
// to schedule (see isRunIncompatibleSource) — this must outrank the Capsule rule below, since
// Capsule flows are only exempt from the *schedule* requirement (NEX-21660), not from these.
// A null source is not one of those reasons (Capsule flows often have none), so it falls
// through to the Capsule rule instead of being excluded here.
const runIncompatibleSourceRule: FlowRunAffordanceRule = ({ source, connectionCategory }) =>
  source && isRunIncompatibleSource(source, connectionCategory)
    ? FLOW_RUN_AFFORDANCE_ACTIVATE_ONLY
    : null;

// Capsule flows have always offered the Run Now / Next Cycle choice (NEX-21660), independent of whether their source has a recurring schedule.
const capsuleAlwaysChoiceRule: FlowRunAffordanceRule = ({ isCapsuleFlow }) =>
  isCapsuleFlow ? FLOW_RUN_AFFORDANCE_ACTIVATE_WITH_CHOICE : null;

const connectorScheduleRule: FlowRunAffordanceRule = ({ source, connectionCategory }) =>
  shouldRenderRunAction(source, connectionCategory)
    ? FLOW_RUN_AFFORDANCE_ACTIVATE_WITH_CHOICE
    : FLOW_RUN_AFFORDANCE_ACTIVATE_ONLY;

const FLOW_RUN_AFFORDANCE_RULES: FlowRunAffordanceRule[] = [
  alreadyRunningRule,
  runIncompatibleSourceRule,
  capsuleAlwaysChoiceRule,
  connectorScheduleRule,
];

export function resolveFlowRunAffordance(input: FlowRunAffordanceInput): FlowRunAffordance {
  for (const rule of FLOW_RUN_AFFORDANCE_RULES) {
    const affordance = rule(input);
    if (affordance) return affordance;
  }

  return FLOW_RUN_AFFORDANCE_ACTIVATE_ONLY;
}
